import {
    Identifiable,
    ImageWarnings,
    LoadingErrorMaybe,
    Warnings,
    ImageProcessingState,
    ImageProcessingColors,
    UploadPresentationOptions,
} from "@nova/constants"
import type { HeatmapStyle } from "@nova/component-heatmap"
import { get } from "nodash"

export function imageProcessingPresentation<
    T extends Identifiable & {
        state: ImageProcessingState
        warnings?: ImageWarnings
        name?: string
    },
>(s: LoadingErrorMaybe<T>, uploadPresentationOptions: UploadPresentationOptions): HeatmapStyle {
    const shapeStyles: Record<string, string> = {}
    if ("loading" in s) {
        return {
            color: ImageProcessingColors.Pending,
            selectedColor: ImageProcessingColors.Selected,
            disabled: false,
            raised: false,
            state: "Pending",
        }
    }
    if ("error" in s) {
        return {
            ...shapeStyles,
            color: ImageProcessingColors.error,
            selectedColor: ImageProcessingColors.Selected,
            disabled: false,
            raised: false,
            state: "Error",
        }
    }
    const warningColor = get(s, "warningColor", "")
    if (!!warningColor) {
        return {
            ...shapeStyles,
            color: warningColor,
            selectedColor: ImageProcessingColors.Selected,
            raised: false,
            disabled: false,
            state: "Staged with Warning",
        }
    }
    if (s.name?.toLowerCase()?.includes(".mp4")) {
        shapeStyles["width"] = "100%"
    }
    if (s.state === ImageProcessingState.Pending) {
        return {
            ...shapeStyles,
            color: ImageProcessingColors.Pending,
            selectedColor: ImageProcessingColors.Selected,
            disabled: false,
            raised: false,
            state: "Pending",
        }
    }

    if (uploadPresentationOptions.uploadingStates?.includes(s.state)) {
        return {
            ...shapeStyles,
            color: ImageProcessingColors.Uploading,
            selectedColor: ImageProcessingColors.Selected,
            disabled: true,
            raised: false,
            state: "Uploading",
        }
    }

    if (uploadPresentationOptions.completeStates.includes(s.state)) {
        return {
            ...shapeStyles,
            color: ImageProcessingColors.Complete,
            selectedColor: ImageProcessingColors.Selected,
            disabled: false,
            raised: true,
            state: "Done Uploading",
        }
    }

    switch (s.state) {
        case ImageProcessingState.Processing:
            return {
                ...shapeStyles,
                color: ImageProcessingColors.Processing,
                selectedColor: ImageProcessingColors.Selected,
                disabled: true,
                raised: false,
                state: "Staging",
            }
        case ImageProcessingState.ProcessingDone:
            if (
                s.warnings &&
                Object.keys(s.warnings).length > 0 &&
                Object.values(s.warnings).some((warning) => warning?.type !== Warnings.Message)
            ) {
                return {
                    ...shapeStyles,
                    color: ImageProcessingColors.warning,
                    selectedColor: ImageProcessingColors.Selected,
                    raised: false,
                    disabled: false,
                    state: "Staged with Warning",
                }
            }
            return {
                ...shapeStyles,
                color: ImageProcessingColors.ProcessingDone,
                selectedColor: ImageProcessingColors.Selected,
                disabled: false,
                raised: true,
                state: "Ready to Upload",
            }
        case ImageProcessingState.PendingUpload:
            return {
                ...shapeStyles,
                color: ImageProcessingColors.ProcessingDone,
                selectedColor: ImageProcessingColors.Selected,
                disabled: false,
                raised: true,
                state: "Staged Upload",
            }
        case ImageProcessingState.Uploading:
            return {
                ...shapeStyles,
                color: ImageProcessingColors.Uploading,
                selectedColor: ImageProcessingColors.Selected,
                disabled: true,
                raised: false,
                state: "Uploading",
            }
    }
    return {
        ...shapeStyles,
        color: ImageProcessingColors.message,
        disabled: false,
        raised: true,
        state: "Ready to Upload",
    }
}
